import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'react-feather';
import { cn } from '@/lib/utils/cn';
import { Label } from '../label/label';

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: React.ReactNode;
  indeterminate?: boolean;
  disabled?: boolean;
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, indeterminate, disabled, ...props }, forwardedRef) => {
    const ref = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (ref.current) {
        ref.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    const IconComponent = indeterminate ? Minus : Check;

    return (
      <div className='flex items-center cursor-pointer'>
        <CheckboxPrimitive.Root
          ref={forwardedRef}
          id={props.id}
          className={cn(
            'ml-1 peer h-4 rounded-[0.1875rem] w-4 shrink-0 border border-border hover:border-gray ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=checked]:bg-blue-1 data-[state=checked]:text-white data-[state=checked]:border-blue-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-disableBg disabled:data-[state=checked]:bg-disableBg disabled:data-[state=checked]:text-disableText disabled:data-[state=checked]:border-none disabled:data-[state=unchecked]:border-disableBorder',
            className
          )}
          disabled={disabled}
          {...props}
        >
          <CheckboxPrimitive.Indicator className='flex items-center justify-center'>
            <IconComponent strokeWidth='3' width={14} height={15} />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {label && (
          <Label
            htmlFor={props.id}
            disabled={disabled}
            className={`${disabled ? 'text-disableText' : 'text-text'}`}
          >
            {label}
          </Label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
export type { CheckboxProps };
